import React, { useState, useRef, useEffect } from 'react';
import DayCard from './DayCard';
import { api } from '../../shared/utils/api';
import { addDays, shortDayLabel, longDayLabel, applyFlkFilter, applySortEntries } from './plannerUtils';

// ─── AddTopicPopover ──────────────────────────────────────────────────────────

function AddTopicPopover({ dateStr, onAdd, onClose, openUp }) {
  const [mode, setMode] = useState(null);
  const [autoError, setAutoError] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  const handleAuto = async () => {
    setMode('auto'); setAutoLoading(true);
    const res = await api('/api/planner/add-topic', {
      method: 'POST', body: JSON.stringify({ date: dateStr, mode: 'auto' }),
    });
    setAutoLoading(false);
    if (res.ok) { onAdd(res.entry); onClose(); } else { setAutoError(res.error || 'No topics available'); }
  };

  const handleSearch = async q => {
    setSearch(q);
    if (q.length < 2) { setTopics([]); return; }
    setTopicsLoading(true);
    const [f1, f2] = await Promise.all([api('/api/topics/FLK1'), api('/api/topics/FLK2')]);
    const all = [...(Array.isArray(f1) ? f1 : []), ...(Array.isArray(f2) ? f2 : [])];
    const ql = q.toLowerCase();
    setTopics(all.filter(t =>
      (t.subtopic || '').toLowerCase().includes(ql) ||
      (t.display_name || '').toLowerCase().includes(ql) ||
      (t.subject_area || '').toLowerCase().includes(ql)
    ).slice(0, 12));
    setTopicsLoading(false);
  };

  const handleChoose = async topic => {
    const res = await api('/api/planner/add-topic', {
      method: 'POST',
      body: JSON.stringify({ date: dateStr, mode: 'choose', subtopic: topic.subtopic, module: topic.module }),
    });
    if (res.ok) { onAdd(res.entry); onClose(); }
  };

  return (
    <div className={`planner-add-popover${openUp ? ' planner-add-popover--up' : ''}`} ref={ref}>
      {mode === null && (
        <>
          <div className="planner-add-popover-title">Add a topic</div>
          <button className="planner-add-opt-btn" onClick={handleAuto}>
            <div className="planner-add-opt-label">Auto-suggest</div>
            <div className="planner-add-opt-desc">Pick the next priority topic</div>
          </button>
          <button className="planner-add-opt-btn" onClick={() => setMode('choose')}>
            <div className="planner-add-opt-label">Choose topic</div>
            <div className="planner-add-opt-desc">Search and pick any topic</div>
          </button>
        </>
      )}
      {mode === 'auto' && autoLoading && <div className="planner-add-loading">Finding next topic</div>}
      {mode === 'auto' && !autoLoading && autoError && <div className="planner-add-error">{autoError}</div>}
      {mode === 'choose' && (
        <>
          <div className="planner-add-popover-title">Choose a topic</div>
          <input className="planner-add-search" autoFocus placeholder="Search topics" value={search} onChange={e => handleSearch(e.target.value)} />
          {topicsLoading && <div className="planner-add-loading">Searching</div>}
          <div className="planner-add-results">
            {topics.map(t => (
              <button key={t.id} className="planner-add-result-btn" onClick={() => handleChoose(t)}>
                <span className="planner-add-result-name">{t.display_name || t.subtopic}</span>
                <span className="planner-add-result-meta">{t.module} · {t.subject_area}</span>
              </button>
            ))}
            {!topicsLoading && topics.length === 0 && search.length >= 2 && (
              <div className="planner-add-noresults">No topics found</div>
            )}
          </div>
          <button className="planner-add-back" onClick={() => setMode(null)}>Back</button>
        </>
      )}
    </div>
  );
}

// ─── DayView (was DayViewPane) ────────────────────────────────────────────────

export default function DayView({ dateStr, dayData, allWeekDates, onComplete, onMove, onAddEntry,
  isToday, isPast, onPrev, onNext, daySort, setDaySort,
  detailLevel, colourMode, hideCompleted, sortSubject,
  flkFilter, onNavigateToTopic, onSkipDay }) {
  const [showAdd, setShowAdd] = useState(false);
  const [skipConfirm, setSkipConfirm] = useState(false);
  const [skipping, setSkipping] = useState(false);

  const handleSkip = async () => {
    setSkipping(true);
    const res = await api('/api/planner/skip-day', {
      method: 'POST',
      body: JSON.stringify({ date: dateStr }),
    });
    setSkipping(false);
    setSkipConfirm(false);
    if (res && !res.error && onSkipDay) onSkipDay(dateStr, res);
  };

  const { is_study_day } = dayData || {};
  const rawEntries = dayData?.entries || [];
  const filtered = applyFlkFilter(rawEntries, flkFilter);
  let sorted = applySortEntries(filtered, sortSubject ? 'subject' : daySort);
  const pending = sorted.filter(e => e.status !== 'completed' && e.status !== 'moved');
  const moved = sorted.filter(e => e.status === 'moved');
  const completed = sorted.filter(e => e.status === 'completed');
  const displayCompleted = hideCompleted ? [] : completed;

  const origTotal = filtered.length;
  const origCompleted = filtered.filter(e => e.status === 'completed').length;

  return (
    <div>
      {/* Day navigation */}
      <div className="day-nav" style={{ marginBottom: 14 }}>
        <button className="day-nav-btn" onClick={onPrev}>← {shortDayLabel(addDays(dateStr, -1))}</button>
        <div className="day-nav-center">
          {isToday && <div className="day-nav-today-badge">Today</div>}
          <div className="day-nav-title">{longDayLabel(dateStr)}</div>
          {origTotal > 0 && <div className="day-nav-count">{origCompleted} of {origTotal} done</div>}
          {pending.length > 0 && !skipConfirm && (
            <button className="skip-day-btn" onClick={() => setSkipConfirm(true)}>
              Skip today
            </button>
          )}
          {skipConfirm && (
            <div className="skip-day-confirm">
              <span className="skip-day-confirm-text">
                Skip {longDayLabel(dateStr)}? {pending.length} topic{pending.length !== 1 ? 's' : ''} will be redistributed across your remaining study days.
              </span>
              <div className="skip-day-confirm-actions">
                <button className="skip-day-confirm-yes" onClick={handleSkip} disabled={skipping}>
                  {skipping ? 'Skipping' : 'Skip'}
                </button>
                <button className="skip-day-confirm-no" onClick={() => setSkipConfirm(false)} disabled={skipping}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        <button className="day-nav-btn" onClick={onNext}>{shortDayLabel(addDays(dateStr, 1))} →</button>
      </div>

      {!dayData || is_study_day === false ? (
        <div className="day-view-rest">Rest day — no sessions scheduled</div>
      ) : (
        <>
          {/* Controls */}
          {origTotal > 0 && (
            <div className="day-controls">
              <div className="day-controls-right">
                <select
                  className="day-sort"
                  value={daySort}
                  onChange={e => setDaySort(e.target.value)}
                >
                  <option value="priority">Priority order</option>
                  <option value="lightest">Lightest first</option>
                  <option value="heaviest">Heaviest first</option>
                </select>
              </div>
            </div>
          )}

          {/* Pending */}
          {pending.length > 0 && (
            <>
              <div className="section-label">To do ({pending.length})</div>
              <div className="day-cards">
                {pending.map(entry => (
                  <DayCard
                    key={entry.id}
                    entry={entry}
                    dateStr={dateStr}
                    allWeekDates={allWeekDates}
                    onComplete={onComplete}
                    onMove={onMove}
                    isPast={isPast}
                    detailLevel={detailLevel}
                    colourMode={colourMode}
                    onNavigateToTopic={onNavigateToTopic}
                  />
                ))}
              </div>
            </>
          )}

          {/* Moved */}
          {moved.length > 0 && (
            <div className="day-cards">
              {moved.map(entry => (
                <DayCard
                  key={entry.id}
                  entry={entry}
                  dateStr={dateStr}
                  allWeekDates={allWeekDates}
                  onComplete={onComplete}
                  onMove={onMove}
                  isPast={isPast}
                  detailLevel={detailLevel}
                  colourMode={colourMode}
                  onNavigateToTopic={onNavigateToTopic}
                />
              ))}
            </div>
          )}

          {pending.length === 0 && moved.length === 0 && displayCompleted.length === 0 && (
            <div className="day-view-empty">
              {isPast ? 'No topics were scheduled for this day.' : 'No topics scheduled. Add one below.'}
            </div>
          )}

          {/* Completed */}
          {displayCompleted.length > 0 && (
            <>
              <div className="section-label">Done ({displayCompleted.length})</div>
              <div className="day-cards">
                {displayCompleted.map(entry => (
                  <DayCard
                    key={entry.id}
                    entry={entry}
                    dateStr={dateStr}
                    allWeekDates={allWeekDates}
                    onComplete={onComplete}
                    onMove={onMove}
                    isPast={isPast}
                    detailLevel={detailLevel}
                    colourMode={colourMode}
                    onNavigateToTopic={onNavigateToTopic}
                  />
                ))}
              </div>
            </>
          )}

          {/* Add topic */}
          {!isPast && (
            <div className="day-view-add-wrap">
              <button className="btn-add" onClick={() => setShowAdd(v => !v)}>
                + Add a topic
              </button>
              {showAdd && (
                <AddTopicPopover
                  dateStr={dateStr}
                  onAdd={entry => { onAddEntry(dateStr, entry); setShowAdd(false); }}
                  onClose={() => setShowAdd(false)}
                  openUp={true}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
